import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import searchBrands from "@tSchaedlerYesco/Handlers/BrandSearchHandler";
import setActiveTab from "@tSchaedlerYesco/Handlers/SearchResultsTabsHandler";
import { BrandModel } from "@tSchaedlerYesco/Services/BrandSearchServices";
import CustomApplicationState from "@tSchaedlerYesco/Store/CustomApplicationState";
import parse from "html-react-parser";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    noDocumentFoundMessage = "noDocumentFoundMessage",
    openLinksInNewWindow = "openLinksInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.noDocumentFoundMessage]: string;
        [fields.openLinksInNewWindow]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState & ApplicationState) => {
    const { productFilters, isSearchPage } = state.pages.productList;
    const {
        brandSearch: { brands, isLoading, requestCompleted, query },
        searchResultsTab,
    } = state.tData;

    return {
        location: getLocation(state),
        brands,
        isLoading,
        isSearchPage,
        query: productFilters.query,
        requestCompleted,
        activeTab: searchResultsTab.activeTab,
        brandsQuery: query,
    };
};

const mapDispatchToProps = {
    searchBrands,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListViewSelectStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    searchQuery?: TypographyPresentationProps;
    documentCount?: TypographyPresentationProps;
    document?: InjectableCss;
    linkStyle?: InjectableCss;
    documentName?: TypographyPresentationProps;
    documentPath?: TypographyPresentationProps;
    documentPathLink?: LinkPresentationProps;
    noSearch?: InjectableCss;
    length?: InjectableCss;
    Border?: InjectableCss;
}

export const viewSelectStyles: ProductListViewSelectStyles = {
    wrapper: {
        css: css`
            margin-top: 8px;
            margin-left: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 20px;
                        margin-left: 0px;
                    `,
                    css`
                        margin-top: 20px;
                        margin-left: 0px;
                    `,
                    css`
                        margin-top: 20px;
                        margin-left: 0px;
                    `,
                    css`
                        margin-top: 8px;
                        margin-left: 0px;
                    `,
                ])}
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    searchQuery: {
        color: "primary",
        weight: 500,
        css: css`
            text-transform: uppercase;
            margin-bottom: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        text-align: center;
                    `,
                    css`
                        text-align: center;
                    `,
                    css`
                        margin-left: 25px;
                    `,
                    css`
                        margin-left: 10px;
                    `,
                ])}
        `,
    },
    documentCount: {
        css: css``,
    },
    document: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    linkStyle: {
        css: css`
            &:focus {
                outline: none !important;
            }
        `,
    },
    documentName: {
        // color: "primary",
        weight: 500,
        css: css`
            color: ${getColor("primary")};
            text-transform: capitalize;
            &:focus {
                outline: none !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 30px;
                    `,
                    css`
                        margin-left: 30px;
                    `,
                    css`
                        margin-left: 30px;
                    `,
                    css`
                        margin-left: 10px;
                    `,
                ])}
        `,
    },
    documentPath: {
        css: css`
            font-style: italic;
            &:focus {
                outline: none !important;
            }
        `,
    },
    documentPathLink: {
        css: css`
            &:focus {
                outline: none !important;
            }
            [class^="TypographyStyle"] {
                color: ${getColor("text.main")};
                font-style: italic;
                margin-right: 8px;
                word-break: break-all;
                &:hover {
                    color: ${getColor("text.link")};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-left: 15px;
                            margin-right: 8px;
                        `,
                        css`
                            margin-left: 15px;
                            margin-right: 8px;
                        `,
                        css`
                            margin-left: 15px;
                            margin-right: 8px;
                        `,
                        css`
                            margin-left: 5px;
                            margin-right: 8px;
                        `,
                    ])}
            }
        `,
    },
    noSearch: {
        css: css`
            margin-left: 20px;
            span {
                color: black;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 30px;
                        margin-right: 5px;
                    `,
                    css`
                        margin-left: 30px;
                        margin-right: 5px;
                    `,
                    css`
                        margin-left: 30px;
                        margin-right: 5px;
                    `,
                    css`
                        margin-left: 20px;
                        margin-right: 5px;
                    `,
                ])}
        `,
    },
    length: {
        css: css`
            margin-left: 5px;
        `,
    },
    Border: {
        css: css`
            border-top: 1px solid #cccccc;
            padding-bottom: 10px;
        `,
    },
};

const styles = viewSelectStyles;

const TECBrandsSearch: FC<Props> = ({
    isSearchPage,
    location,
    brands,
    isLoading,
    searchBrands,
    fields,
    query,
    requestCompleted,
    activeTab,
    brandsQuery,
}) => {
    const toasterContext = useContext(ToasterContext);
    useEffect(() => {
        const queryParams = parseQueryString<{ activeTab?: string }>(window.location.search);
        setActiveTab({
            activeTab: queryParams.activeTab,
        });
        if (brandsQuery !== query || !requestCompleted) {
            searchBrands({
                query,
                onError: message => {
                    toasterContext.addToast({ body: message, messageType: "danger" });
                },
            });
        }
    }, [location.search, query]);

    if (!isSearchPage || activeTab !== "brand") {
        return null;
    }

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    const loc = window.location;

    return (
        <StyledWrapper {...styles.wrapper}>
            {!brands ? (
                <Typography as="h2" {...styles.noSearch}>
                    {parse(fields.noDocumentFoundMessage, parserOptions)}
                </Typography>
            ) : (
                <>
                    <Typography {...styles.length}>{`${brands.length} items for `}</Typography>
                    {query && (
                        <Typography {...styles.documentCount} data-test-selector="productListQueryText">
                            {query}
                        </Typography>
                    )}

                    <StyledWrapper {...styles.Border}></StyledWrapper>

                    <StyledWrapper>
                        {brands.map((brand: BrandModel) => {
                            return (
                                <StyledWrapper key={brand.title} {...styles.document}>
                                    <Link
                                        {...styles.linkStyle}
                                        href={brand.url}
                                        target={fields.openLinksInNewWindow ? "_blank" : "_self"}
                                    >
                                        <Typography as="h3" {...styles.documentName}>
                                            {brand.title}
                                        </Typography>
                                    </Link>
                                    <StyledWrapper>
                                        <Link
                                            href={brand.url}
                                            {...styles.documentPathLink}
                                            target={fields.openLinksInNewWindow ? "_blank" : "_self"}
                                        >
                                            <Typography as="p" {...styles.documentPath}>
                                                {loc.origin}
                                                {brand.url}
                                            </Typography>
                                        </Link>
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(TECBrandsSearch),
    definition: {
        group: "Product List",
        displayName: "BrandsSearch",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.noDocumentFoundMessage,
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.openLinksInNewWindow,
                displayName: "openLinksInNewWindow",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
